// DefaultLocationModal.jsx
// A self-contained modal button component for setting default location coordinates
// from a map URL, showing only visibility-allowed employees.
// Usage: <DefaultLocationModal theme={theme} language={language} isRTL={isRTL} />

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { MapPin, X, Search, CheckCircle, AlertCircle, Loader, Link, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import { useProfile } from "../../../../hooks/useProfile";
import useScreenSize from "../../../../hooks/useScreenSize";
import { makeStyles, scrollbarCss } from "../shiftControl/shiftControlStyles";
// أضف هذا الاستيراد في أعلى الملف
import ReactDOM from "react-dom";
const BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";

// ─── translations ────────────────────────────────────────────────────────────
const t = {
  en: {
    btnLabel: "Default Location",
    modalTitle: "Set Default Location",
    modalSubtitle: "Set a default map coordinate for an employee using a Google Maps link.",
    searchPlaceholder: "Search by name or ID…",
    noEmployees: "No employees found.",
    urlLabel: "Google Maps URL",
    urlPlaceholder: "Paste Google Maps link here…",
    radiusLabel: "Radius (m)",
    namePlaceholder: "Location name (optional, e.g. HQ, Branch)",
    addLocation: "Add another location",
    locationN: "Location",
    removeLocation: "Remove location",
    loadingLocations: "Loading saved locations…",
    noLocations: "No default locations saved yet. Add one below.",
    save: "Save Location",
    saving: "Saving…",
    cancel: "Cancel",
    successMsg: "Default locations saved successfully!",
    clearedMsg: "All default locations cleared.",
    errorMsg: "Failed to save coordinates.",
    urlRequired: "Please paste at least one map URL.",
    empRequired: "Please select an employee first.",
    selected: "Selected",
    coords: "Coordinates",
    loading: "Loading…",
    supportedHint: "Supported: Google Maps links, short links (goo.gl/maps), share URLs with @lat,lng, etc.",
    change: "Change",
    pickEmployee: "Choose an employee",
    pickEmployeeHint: "Choose an employee from the list to set their default locations.",
    nameLabel: "Location name (optional)",
    radiusHint: "Check-ins within this distance count as on-site.",
  },
  ar: {
    btnLabel: "الموقع الافتراضي",
    modalTitle: "تعيين الموقع الافتراضي",
    modalSubtitle: "قم بتعيين إحداثيات خريطة افتراضية للموظف باستخدام رابط Google Maps.",
    searchPlaceholder: "ابحث بالاسم أو الرقم…",
    noEmployees: "لا يوجد موظفون.",
    urlLabel: "رابط Google Maps",
    urlPlaceholder: "الصق رابط Google Maps هنا…",
    radiusLabel: "النطاق (متر)",
    namePlaceholder: "اسم الموقع (اختياري، مثل: المقر، الفرع)",
    addLocation: "إضافة موقع آخر",
    locationN: "الموقع",
    removeLocation: "حذف الموقع",
    loadingLocations: "جارٍ تحميل المواقع المحفوظة…",
    noLocations: "لا توجد مواقع افتراضية محفوظة بعد. أضف واحدًا أدناه.",
    save: "حفظ الموقع",
    saving: "جارٍ الحفظ…",
    cancel: "إلغاء",
    successMsg: "تم حفظ المواقع الافتراضية بنجاح!",
    clearedMsg: "تم مسح جميع المواقع الافتراضية.",
    errorMsg: "فشل حفظ الإحداثيات.",
    urlRequired: "الرجاء لصق رابط خريطة واحد على الأقل.",
    empRequired: "الرجاء اختيار موظف أولاً.",
    selected: "المحدد",
    coords: "الإحداثيات",
    loading: "جارٍ التحميل…",
    supportedHint: "المدعوم: روابط Google Maps، والروابط المختصرة (goo.gl/maps)، وروابط المشاركة التي تحتوي على ‎@lat,lng‎ وغيرها.",
    change: "تغيير",
    pickEmployee: "اختر موظفًا",
    pickEmployeeHint: "اختر موظفًا من القائمة لتعيين مواقعه الافتراضية.",
    nameLabel: "اسم الموقع (اختياري)",
    radiusHint: "البصمة داخل هذه المسافة تُحسب من داخل الموقع.",
  },
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const resolveManagerContext = () => {
  try {
    const token = localStorage.getItem("token");
    let payload = null;
    if (token) {
      const parts = token.split(".");
      if (parts.length === 3) {
        let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4) b64 += "=";
        payload = JSON.parse(atob(b64));
      }
    }
    return {
      clientName:
        localStorage.getItem("clientName") ||
        payload?.clientName ||
        payload?.employeeClientName ||
        "",
      managerCode:
        localStorage.getItem("infinityCode") ||
        payload?.infinityCode ||
        payload?.employeeInfinityCode ||
        null,
    };
  } catch {
    return { clientName: "", managerCode: null };
  }
};

// ─── main component ───────────────────────────────────────────────────────────
// `hideTrigger` drops the button; the phone action menu opens it through the ref.
const DefaultLocationModal = forwardRef(({ theme = "light", language = "en", isRTL = false, hideTrigger = false }, ref) => {
  const tr = t[language] || t.en;
  const isDark = theme === "dark";
  const { profile } = useProfile();
  const { isMobile } = useScreenSize();
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  // modal visibility
  const [open, setOpen] = useState(false);
  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }), []);

  // employee list
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [search, setSearch] = useState("");

  // selection
  const [selectedEmp, setSelectedEmp] = useState(null);

  // default locations. An employee may have more than one.
  // Existing entries: { isExisting: true, name, latitude, longitude, radius }.
  // New entries:      { isExisting: false, name, mapUrl, radius }.
  const newEntry = () => ({ isExisting: false, name: "", mapUrl: "", radius: "500" });
  const [locations, setLocations] = useState([newEntry()]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const updateLocation = (idx, patch) => {
    setLocations((prev) => prev.map((loc, i) => (i === idx ? { ...loc, ...patch } : loc)));
    setFeedback(null);
  };
  const addLocation = () => {
    setLocations((prev) => [...prev, newEntry()]);
    setFeedback(null);
  };
  const removeLocation = (idx) => {
    setLocations((prev) => prev.filter((_, i) => i !== idx));
    setFeedback(null);
  };

  // save state
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', msg: string }
  const [allowDefaultLocation, setAllowDefaultLocation] = useState(true);

  // ── load employees when modal opens ────────────────────────────────────────
  const loadEmployees = useCallback(async () => {
    setLoadingEmps(true);
    setEmployees([]);
    try {
      const { clientName: fallbackClient, managerCode: fallbackCode } = resolveManagerContext();
      const clientName = profile?.clientName || fallbackClient;
      const managerCode = profile?.infinityCode || fallbackCode;
      const viewType = localStorage.getItem("managerViewType") || "manager";

      const usp = new URLSearchParams();
      if (clientName) usp.append("clientName", clientName);
      usp.append("viewType", viewType);
      if (viewType === "manager" && managerCode)
        usp.append("managerInfinityCode", managerCode);
      usp.append("forDefaultLocation", "true");

      const resp = await apiFetch(
        `${BASE_URL}/employees/visibility?${usp.toString()}`,
        { method: "GET", headers: { "Accept-Language": language } }
      );
      if (!resp.ok) throw new Error("fetch failed");
      const json = await resp.json();
      const list = json.employees || json.data || [];
      setEmployees(
        list.map((e) => ({
          infinityCode: e.infinityCode || e.id,
          clientCode: e.clientCode || e.infinityCode || e.id,
          name:
            language === "ar"
              ? e.arabicName || e.sr || e.name || String(e.clientCode || e.infinityCode)
              : e.sr || e.name || e.arabicName || String(e.clientCode || e.infinityCode),
          clientName: e.clientName,
        }))
      );
    } catch {
      setEmployees([]);
    } finally {
      setLoadingEmps(false);
    }
  }, [language]);

  useEffect(() => {
    if (open) {
      loadEmployees();
      setSelectedEmp(null);
      setLocations([newEntry()]);
      setFeedback(null);
      setSearch("");
      setAllowDefaultLocation(true);
    }
  }, [open, loadEmployees]);

  // ── load the selected employee's existing default locations ─────────────────
  useEffect(() => {
    if (!selectedEmp) return;
    let mounted = true;
    (async () => {
      setLoadingLocations(true);
      setFeedback(null);
      try {
        const resp = await apiFetch(
          `${BASE_URL}/employees/location/${selectedEmp.infinityCode}/default-coordinates`,
          { method: "GET", headers: { "Accept-Language": language } }
        );
        const data = await resp.json();
        if (!mounted) return;
        const existing = (resp.ok && data.success ? data.data?.defaultLocations : null) || [];
        const isAllowed = data.data?.allowDefaultLocation ?? true;
        setAllowDefaultLocation(isAllowed);
        if (!isAllowed) {
          setFeedback({
            type: "error",
            msg: language === "ar"
              ? "تنبيه: ميزة الموقع الافتراضي معطلة لهذا الموظف في إعدادات الشركة."
              : "Warning: Default Location feature is disabled for this employee in Company Configuration.",
          });
        }
        setLocations(
          existing.length
            ? existing.map((loc) => ({
                isExisting: true,
                name: loc.name ?? "",
                latitude: loc.latitude,
                longitude: loc.longitude,
                radius: String(loc.radius ?? 500),
              }))
            : [newEntry()]
        );
      } catch {
        if (mounted) setLocations([newEntry()]);
      } finally {
        if (mounted) setLoadingLocations(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedEmp, language]);

  // ── filtered list ──────────────────────────────────────────────────────────
  const filtered = employees.filter((e) => {
    const q = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      String(e.clientCode || e.infinityCode).toLowerCase().includes(q)
    );
  });

  // ── save handler ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setFeedback(null);
    if (!allowDefaultLocation) {
      setFeedback({
        type: "error",
        msg: language === "ar"
          ? "تنبيه: ميزة الموقع الافتراضي معطلة لهذا الموظف في إعدادات الشركة."
          : "Default Location feature is disabled for this employee in Company Configuration.",
      });
      return;
    }
    if (!selectedEmp) {
      setFeedback({ type: "error", msg: tr.empRequired });
      return;
    }
    const payloadLocations = locations
      .filter((loc) => loc.isExisting || loc.mapUrl.trim())
      .map((loc) => {
        const radius = Number(loc.radius) > 0 ? Number(loc.radius) : 500;
        const name = loc.name?.trim() || null;
        return loc.isExisting
          ? { name, latitude: loc.latitude, longitude: loc.longitude, radius }
          : { name, mapUrl: loc.mapUrl.trim(), radius };
      });

    // An empty list is allowed: it clears all default locations.
    setSaving(true);
    try {
      const resp = await apiFetch(
        `${BASE_URL}/employees/location/${selectedEmp.infinityCode}/default-coordinates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": language,
          },
          body: JSON.stringify({ locations: payloadLocations }),
        }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || "error");

      const count = data.data?.defaultLocations?.length ?? payloadLocations.length;
      setFeedback({
        type: "success",
        msg: count === 0 ? tr.clearedMsg : `${tr.successMsg} (${count})`,
      });
      // auto-close after 2.5 s
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      setFeedback({ type: "error", msg: err.message || tr.errorMsg });
    } finally {
      setSaving(false);
    }
  };

  // ── styles: same dialog shell as Shift Control / Remote Punch ──────────────
  const st = makeStyles(theme, isRTL, isMobile);
  const accent = st.accent;
  const border = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)";
  const textPrimary = st.textPrimary;
  const textSecondary = st.muted;
  const inputStyle = { ...st.input, width: "100%", fontSize: 14 };
  const close = () => !saving && setOpen(false);
  const empCode = (emp) => emp.clientCode || emp.infinityCode;
  // Phones: pick an employee first, then the locations replace the list.
  const showList = !isMobile || !selectedEmp;
  const showForm = !isMobile || !!selectedEmp;

  const initialOf = (emp) => (emp.name || "?").charAt(0).toUpperCase();
  const avatar = (emp, active, size = 38) => (
    <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size > 36 ? 15 : 13, fontWeight: 700, color: active ? "#fff" : textSecondary, background: active ? accent : isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)" }}>
      {initialOf(emp)}
    </div>
  );

  const listPane = (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, ...(!isMobile && { borderInlineEnd: `1px solid ${border}` }) }}>
      <div style={{ padding: isMobile ? "0 0 10px" : "16px 16px 10px", flexShrink: 0 }}>
        {isMobile && <div style={{ ...st.fieldLabel, marginBottom: 8 }}>{tr.pickEmployee}</div>}
        <div style={{ position: "relative" }}>
          <Search size={16} style={{ position: "absolute", top: "50%", insetInlineStart: 13, transform: "translateY(-50%)", color: textSecondary, pointerEvents: "none" }} />
          <input
            type="text"
            className="sc-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr.searchPlaceholder}
            style={{ ...inputStyle, paddingInlineStart: 38 }}
          />
        </div>
      </div>
      <div className="sc-noscroll" style={{ flex: 1, minHeight: 0, overflowY: isMobile ? "visible" : "auto", padding: isMobile ? 0 : "4px 10px 16px" }}>
        {loadingEmps ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 160, gap: 12, color: textSecondary }}>
            <Loader size={28} style={{ animation: "spin 1s linear infinite" }} />
            <span style={{ fontSize: 13 }}>{tr.loading}</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "40px 16px", textAlign: "center", color: textSecondary, fontSize: 13 }}>{tr.noEmployees}</div>
        ) : (
          filtered.map((emp) => {
            const isActive = selectedEmp?.infinityCode === emp.infinityCode;
            return (
              <button
                type="button"
                key={emp.infinityCode}
                onClick={() => { setSelectedEmp(emp); setFeedback(null); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", marginBottom: 4,
                  borderRadius: 10, cursor: "pointer", textAlign: "start", fontFamily: "inherit", color: textPrimary,
                  background: isActive ? (isDark ? "rgba(46,124,186,0.25)" : "rgba(46,124,186,0.10)") : isMobile ? surfaceAlt : "transparent",
                  border: isActive ? `1px solid ${accent}55` : isMobile ? `1px solid ${border}` : "1px solid transparent",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = surfaceAlt; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = isMobile ? surfaceAlt : "transparent"; }}
              >
                {avatar(emp, isActive, 36)}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: isActive ? accent : textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.name}</span>
                  <span style={{ display: "block", fontSize: 12, color: textSecondary, marginTop: 1 }}>#{empCode(emp)}</span>
                </span>
                {isActive ? <CheckCircle size={18} color={accent} style={{ flexShrink: 0 }} /> : isMobile && <ChevronIcon size={18} color={textSecondary} style={{ flexShrink: 0 }} />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const formPane = (
    <div className="sc-noscroll" style={{ display: "flex", flexDirection: "column", gap: 16, padding: isMobile ? 0 : "20px 24px", overflowY: isMobile ? "visible" : "auto", minWidth: 0 }}>
      {/* selected employee */}
      <div style={{ padding: "12px 14px", borderRadius: 12, minHeight: 58, display: "flex", alignItems: "center", gap: 12, background: selectedEmp ? (isDark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.07)") : surfaceAlt, border: `1px solid ${selectedEmp ? `${accent}44` : border}` }}>
        {selectedEmp ? (
          <>
            {avatar(selectedEmp, true)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: textSecondary }}>{tr.selected}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedEmp.name}</div>
              <div style={{ fontSize: 12, color: textSecondary }}>#{empCode(selectedEmp)}</div>
            </div>
            {isMobile && (
              <button type="button" className="sc-ghost" style={{ ...st.btn("ghost"), minHeight: 36, padding: "6px 12px" }} onClick={() => { setSelectedEmp(null); setFeedback(null); }}>
                {tr.change}
              </button>
            )}
          </>
        ) : (
          <span style={{ fontSize: 13, color: textSecondary }}>{tr.pickEmployeeHint}</span>
        )}
      </div>

      {selectedEmp && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {loadingLocations ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 20, color: textSecondary, fontSize: 13 }}>
              <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
              {tr.loadingLocations}
            </div>
          ) : (
            <>
              {locations.length === 0 && (
                <p style={{ fontSize: 13, color: textSecondary, margin: 0 }}>{tr.noLocations}</p>
              )}
              {locations.map((loc, idx) => (
                <div key={idx} style={{ border: `1px solid ${border}`, borderRadius: 14, padding: 14, background: surfaceAlt, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: textPrimary }}>
                      <span style={{ width: 24, height: 24, borderRadius: "50%", background: accent, color: "#fff", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{idx + 1}</span>
                      {tr.locationN} {idx + 1}
                    </span>
                    <button type="button" onClick={() => removeLocation(idx)} title={tr.removeLocation} aria-label={tr.removeLocation}
                      style={{ ...st.btn("danger"), minHeight: 34, padding: "6px 10px", fontSize: 12.5 }}>
                      <Trash2 size={15} /> {!isMobile && tr.removeLocation}
                    </button>
                  </div>

                  <label style={st.label}>{tr.nameLabel}</label>
                  <input type="text" className="sc-input" value={loc.name} onChange={(e) => updateLocation(idx, { name: e.target.value })} placeholder={tr.namePlaceholder} style={inputStyle} />

                  {loc.isExisting ? (
                    <>
                      <label style={{ ...st.label, display: "flex", alignItems: "center", gap: 6 }}><MapPin size={14} /> {tr.coords}</label>
                      <div style={{ ...inputStyle, display: "flex", alignItems: "center", fontFamily: "monospace", direction: "ltr", opacity: 0.85 }}>
                        {Number(loc.latitude).toFixed(6)}, {Number(loc.longitude).toFixed(6)}
                      </div>
                    </>
                  ) : (
                    <>
                      <label style={{ ...st.label, display: "flex", alignItems: "center", gap: 6 }}><Link size={14} /> {tr.urlLabel}</label>
                      <textarea
                        className="sc-input"
                        value={loc.mapUrl}
                        onChange={(e) => updateLocation(idx, { mapUrl: e.target.value })}
                        placeholder={tr.urlPlaceholder}
                        rows={isMobile ? 2 : 3}
                        style={{ ...inputStyle, resize: "vertical", direction: "ltr", fontFamily: "monospace", fontSize: 13 }}
                      />
                      <div style={{ ...st.hint, marginTop: 4 }}>{tr.supportedHint}</div>
                    </>
                  )}

                  <label style={st.label}>{tr.radiusLabel}</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input type="number" min={1} className="sc-input" value={loc.radius} onChange={(e) => updateLocation(idx, { radius: e.target.value })} style={{ ...inputStyle, width: 140, flexShrink: 0 }} />
                    <span style={st.hint}>{tr.radiusHint}</span>
                  </div>
                </div>
              ))}

              <button type="button" onClick={addLocation}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, minHeight: 44, padding: "10px 14px", borderRadius: 12, border: `1px dashed ${accent}`, background: "transparent", color: accent, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                <Plus size={16} />
                {tr.addLocation}
              </button>
            </>
          )}
        </div>
      )}

      {feedback && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10, background: feedback.type === "success" ? (isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.08)") : (isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.08)"), border: `1px solid ${feedback.type === "success" ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}` }}>
          {feedback.type === "success"
            ? <CheckCircle size={16} color="#10b981" style={{ flexShrink: 0, marginTop: 1 }} />
            : <AlertCircle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />}
          <span style={{ fontSize: 13, fontWeight: 500, color: feedback.type === "success" ? "#10b981" : "#ef4444", lineHeight: 1.4 }}>{feedback.msg}</span>
        </div>
      )}
    </div>
  );

  const saveDisabled = saving || !allowDefaultLocation || !selectedEmp;

  return (
    <>
      {/* ── trigger button (matches sibling action buttons in Attendance.jsx) ── */}
      {!hideTrigger && (
        <button
          onClick={() => setOpen(true)}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.15)", fontWeight: 500, fontSize: 13, cursor: "pointer",
            transition: "all 0.2s ease",
            background: isDark ? "rgba(30,41,59,0.8)" : "rgba(51,65,85,0.12)",
            color: isDark ? "#f1f5f9" : "#1e293b",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(30,41,59,1)" : "rgba(51,65,85,0.2)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? "rgba(30,41,59,0.8)" : "rgba(51,65,85,0.12)"; }}
        >
          <MapPin size={16} />
          {tr.btnLabel}
        </button>
      )}

      {/* ── modal (portaled: page headers use backdrop-filter) ── */}
      {open && ReactDOM.createPortal(
        <div onClick={close} style={{ ...st.overlay, zIndex: 999999 }}>
          <style>{scrollbarCss(isDark)}</style>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={tr.modalTitle}
            onClick={(e) => e.stopPropagation()}
            style={{ ...st.dialog, width: "min(900px, 100%)", height: isMobile ? "94dvh" : "min(720px, calc(100vh - 24px))" }}
          >
            {/* header */}
            <div style={{ ...st.header, flexWrap: "nowrap", alignItems: "flex-start", gap: 12 }}>
              <div style={{ display: "flex", gap: isMobile ? 12 : 14, alignItems: "center", minWidth: 0, flex: "1 1 auto" }}>
                <div style={st.headerIcon}><MapPin size={isMobile ? 20 : 22} /></div>
                <div style={{ minWidth: 0 }}>
                  <h2 style={st.title}>{tr.modalTitle}</h2>
                  <p style={st.subtitle}>{tr.modalSubtitle}</p>
                </div>
              </div>
              <button type="button" className="sc-close" onClick={close} aria-label={tr.cancel}
                style={{ ...st.closeBtn, cursor: saving ? "not-allowed" : "pointer" }}>
                <X size={22} />
              </button>
            </div>

            {/* body: list | form on desktop, one step at a time on phones */}
            <div
              className="sc-noscroll"
              style={isMobile
                ? { ...st.body, display: "block" }
                : { flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "minmax(260px, 2fr) 3fr", overflow: "hidden" }}
            >
              {showList && listPane}
              {showForm && formPane}
            </div>

            {/* footer */}
            {showForm && (
              <div style={{ ...st.footer, justifyContent: "flex-end", flexWrap: "nowrap" }}>
                <button type="button" className="sc-ghost" onClick={close} disabled={saving}
                  style={{ ...st.btn("ghost", saving), ...(isMobile && { flex: "1 1 0" }) }}>
                  {tr.cancel}
                </button>
                <button type="button" className="sc-primary" onClick={handleSave} disabled={saveDisabled}
                  style={{ ...st.btn("primary", saveDisabled), ...(isMobile && { flex: "1 1 0" }) }}>
                  {saving ? <Loader size={15} style={{ animation: "spin 1s linear infinite" }} /> : <MapPin size={15} />}
                  {saving ? tr.saving : tr.save}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* spin keyframe */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Webkit scrollbar styling (Chrome, Edge, Safari) */
        div::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        div::-webkit-scrollbar-track {
          background: ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
          border-radius: 10px;
        }

        div::-webkit-scrollbar-thumb {
          background: ${isDark ? "rgba(148,163,184,0.6)" : "rgba(100,116,139,0.5)"};
          border-radius: 10px;
          border: 2px solid ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
        }

        div::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? "rgba(148,163,184,0.8)" : "rgba(100,116,139,0.7)"};
        }
      `}</style>
    </>
  );
});

export const defaultLocationLabel = (language) => (t[language] || t.en).btnLabel;

export default DefaultLocationModal;